"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.wait = wait;
exports.toHump = toHump;
exports.openInNewTab = openInNewTab;
exports.captureName = captureName;
exports.openInTab = openInTab;
exports.mobileType = mobileType;
exports.generateId = generateId;
exports.keyToHump = keyToHump;
exports.toKebabCase = toKebabCase;
exports.keyToLine = keyToLine;
exports.toLine = toLine;
exports.jwtTokenExpired = jwtTokenExpired;
exports.parseJwtToken = parseJwtToken;
exports.parseJson = parseJson;
exports.findPropertyKeys = findPropertyKeys;
exports.parse10To32 = parse10To32;
exports.parse32To10 = parse32To10;
exports.padStr = padStr;
exports.randomStr = randomStr;
exports.randomNumStr = randomNumStr;
exports.flatten = flatten;
exports.parseMessage = parseMessage;
exports.getLocalTimezone = getLocalTimezone;
exports.uniqBy = uniqBy;
exports.tick = tick;
exports.chainPromises = chainPromises;
exports.maxBy = maxBy;
exports.groupBy = groupBy;
exports.keyBy = keyBy;
exports.debounce = debounce;
exports.get = get;
const base64_1 = require("./base64");
const logs_1 = require("./logs");
async function wait(time) {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve('');
        }, time);
    });
}
function toHump(name) {
    return name.replace(/\_(\w)/g, (_all, letter) => {
        return letter.toUpperCase();
    });
}
function openInNewTab(href) {
    Object.assign(document.createElement('a'), {
        target: '_blank',
        href,
    }).click();
}
function captureName(name) {
    return name.charAt(0).toUpperCase() + name.slice(1);
}
function openInTab(href) {
    Object.assign(document.createElement('a'), {
        href,
    }).click();
}
function mobileType() {
    const u = navigator.userAgent;
    const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Linux') > -1;
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
    if (isAndroid) {
        return 'android';
    }
    if (isIOS) {
        return 'ios';
    }
    return 'unkonw';
}
let counter = 0;
function generateId() {
    counter++;
    const id = `${Date.now().toString(32)}${counter.toString(32).padStart(4, '0')}`;
    return id;
}
function keyToHump(data) {
    return Object.keys(data).reduce((r, k) => {
        return { ...r, [toHump(k)]: data[k] };
    }, {});
}
function toKebabCase(name) {
    return name
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
        .toLowerCase();
}
function keyToLine(data) {
    return Object.keys(data).reduce((r, k) => {
        return { ...r, [toLine(k)]: data[k] };
    }, {});
}
function toLine(name) {
    if (/_/.test(name)) {
        return name;
    }
    return name.replace(/([A-Z])/g, '_$1').toLowerCase();
}
function jwtTokenExpired(token) {
    const tokenInfo = parseJwtToken(token);
    if (tokenInfo?.exp && tokenInfo.exp * 1000 < Date.now()) {
        return false;
    }
    return true;
}
function parseJwtToken(tokenString) {
    if (!tokenString) {
        return {};
    }
    const [, payload] = tokenString.split('.');
    return {
        ...JSON.parse((0, base64_1.base64Decode)(payload).toString()),
    };
}
function parseJson(str) {
    try {
        return JSON.parse(str);
    }
    catch (error) {
        logs_1.logs.error(error);
        return {};
    }
}
function findPropertyKeys(instance) {
    const keys = Object.keys(instance);
    const propertyKeys = Object.getOwnPropertyNames(instance.constructor.prototype).filter((name) => name !== 'constructor');
    return [...keys, ...propertyKeys];
}
function parse10To32(num) {
    return num.toString(32);
}
function parse32To10(numStr) {
    return parseInt(numStr, 32);
}
function padStr(str, num, fillString = '0') {
    return str.padStart(num, fillString);
}
function randomStr(num) {
    let str = '';
    while (str.length < num) {
        str += Math.random().toString(36).slice(2);
    }
    return str.slice(0, num);
}
function randomNumStr(num) {
    let str = '';
    while (str.length < num) {
        str += Math.random().toString().slice(2);
    }
    return str.slice(0, num);
}
function flatten(list) {
    return (list?.reduce((res, item) => {
        return Array.isArray(item) ? [...res, ...item] : [...res, item];
    }, []) || []);
}
function parseMessage(message, data) {
    let msg = message;
    const placeholders = message.match(/\{[^{|^}]*\}/g) || [];
    for (const placeholder of placeholders) {
        const key = placeholder.replace(/[{|}|\s}]/g, '');
        msg = msg.replace(placeholder, data?.[key] || '');
    }
    return msg;
}
function getLocalTimezone() {
    let offset = new Date().getTimezoneOffset() / 60;
    return `Etc/GMT${offset > 0 ? '+' : '-'}${Math.abs(offset)}`;
}
function uniqBy(list, key) {
    const keyMap = {};
    return list.reduce((res, item) => {
        if (keyMap[item[key]]) {
            return res;
        }
        keyMap[item[key]] = true;
        return res.concat(item);
    }, []);
}
async function tick(cb, ms, maxTimes = 10, time = 1) {
    if (time && time > maxTimes) {
        throw new Error('more than max times');
    }
    await wait(ms);
    const res = await cb();
    if (res !== undefined) {
        return res;
    }
    return tick(cb, ms, maxTimes, time++);
}
async function chainPromises(items, callback, initialValue) {
    return items.reduce(async (accPromise, item, index) => {
        const accumulated = await accPromise;
        const result = await callback(item, index);
        return [...accumulated, result];
    }, Promise.resolve(initialValue || []));
}
function maxBy(array, iteratee) {
    if (!array || array.length === 0) {
        return undefined;
    }
    let maxElement = array[0];
    let maxValue = iteratee(maxElement);
    for (const item of array) {
        const value = iteratee(item);
        if (value > maxValue) {
            maxValue = value;
            maxElement = item;
        }
    }
    return maxElement;
}
function groupBy(array, key) {
    return array.reduce((result, item) => {
        const groupKey = item[key];
        if (!result[groupKey]) {
            result[groupKey] = [];
        }
        result[groupKey].push(item);
        return result;
    }, {});
}
function keyBy(array, key) {
    return array.reduce((result, item) => {
        const keyValue = item[key];
        result[keyValue] = item;
        return result;
    }, {});
}
function debounce(func, wait) {
    let timeout = null;
    return function (...args) {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
}
function get(obj, path, defaultValue) {
    if (!path)
        return obj || defaultValue;
    const paths = Array.isArray(path) ? path : [path];
    const length = paths.length;
    let _obj = obj;
    let index = 0;
    while (_obj != null && index < length) {
        const key = paths[index++];
        _obj = _obj[key];
    }
    return _obj || defaultValue;
}
//# sourceMappingURL=utils.js.map